<template>
  <div
    id="gp-app-wrapper"
    class="app-wrapper h-100 bg-f8f8f8"
    :class="{ 'fullscreen-page': isFullscreenPage }"
  >
    <div class="app h-100">
      <div
        v-if="$auth.ready()"
        :class="{ scrollable: isSurveyPage }"
        class="h-100 flex-container sp-scroll"
      >
        <div
          v-if="
            isLoggedIn &&
            currentUser &&
            $route.meta &&
            $route.meta.auth &&
            !$route.meta.fullscreen
          "
          class="h-100 position-relative"
        >
          <div
            id="gp-section-layout"
            class="section-layout h-100 d-flex flex-column bg-f8f8f8"
          >
            <site-header></site-header>
            <div
              class="section-layout__content h-100 pr-5 pl-5 pt-80 position-relative"
            >
              <skill-growth-side-bar></skill-growth-side-bar>
              <router-view v-slot="{ Component, route }">
                <keep-alive :max="2">
                  <component
                    :is="Component"
                    v-if="route.meta.keepAlive !== false"
                    :key="routerViewKey"
                  ></component>
                </keep-alive>
                <component
                  :is="Component"
                  v-if="route.meta.keepAlive === false"
                  :key="routerViewKey"
                ></component>
              </router-view>
              <skill-step-side-bar
                v-if="needToDisplaySkillStepSideBar"
                :skill-id="skillId"
              ></skill-step-side-bar>
            </div>
          </div>
        </div>
        <router-view v-else :key="routerViewKey"></router-view>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-facing-decorator'
import { State, namespace } from 'vuex-facing-decorator'
import type { User } from '@/model/user/schema'
import SiteHeader from '@/components/domain/site/SiteHeader.vue'
import SiteSideBar from '@/components/domain/site/SiteSideBar.vue'
import SkillGrowthSideBar from '@/components/domain/skill/SkillGrowthSideBar.vue'
import SkillStepSideBar from '@/components/domain/skill/SkillStepSideBar.vue'

const AuthStore = namespace('auth')

@Component({
  components: {
    SiteHeader,
    SiteSideBar,
    SkillGrowthSideBar,
    SkillStepSideBar
  }
})
export default class App extends Vue {
  @State appName: string
  @AuthStore.Getter isLoggedIn: boolean
  @AuthStore.Getter currentUser: User

  // mounted
  mounted() {
    const location = new URL(window.location.href)
    const hostname = location.hostname
    if (hostname === 'app.growthpad.jp') {
      window.location.href =
        location.protocol + '//app.grtx.jp' + location.pathname
    } else if (hostname === 'stg.growthpad.jp') {
      window.location.href =
        location.protocol + '//stg-app.grtx.jp' + location.pathname
    }
    console.log(location.protocol + '//stg-app.grtx.jp' + location.pathname)

    // clear expired data in local storage
    this.$utils.localStorage.clearExpiredData()
  }

  // get bgColorSelector() {
  //   let appWrapper = document.getElementById('gp-app-wrapper')
  //   let sectionLayout = document.getElementById('gp-section-layout')
  //
  //   if (
  //     this.$route.name === 'InCompany' ||
  //     this.$route.query.isPrivate === 'true'
  //   ) {
  //     if (appWrapper && sectionLayout) {
  //       appWrapper.classList.remove('bg-f8f8f8')
  //       sectionLayout.classList.remove('bg-f8f8f8')
  //     }
  //     return 'bg-333333'
  //   } else if (this.isSurveyPage) {
  //     return 'bg-fff'
  //   } else {
  //     if (appWrapper && sectionLayout) {
  //       appWrapper.classList.remove('bg-333333')
  //       sectionLayout.classList.remove('bg-333333')
  //     }
  //     return 'bg-f8f8f8'
  //   }
  // }

  // ルートメタに pageGroup があるルート同士（例: /ai-chat/:slug ↔ /ai-chat/:slug/:shareToken）は
  // 同じ key にしてコンポーネントを使い回す。path を key にすると、チャット中のURL正規化のたびに
  // インスタンスが作り直されて状態（ストリーミング等）が失われるため。
  get routerViewKey(): string {
    const meta = this.$route.meta
    if (meta && meta.pageGroup) {
      return meta.pageGroup + ':' + (this.$route.params.slug || '')
    }
    return this.$route.path
  }

  get skillId() {
    // cast params.skillId to string
    return this.$route.params.skillId && String(this.$route.params.skillId)
  }

  get needToDisplaySkillStepSideBar() {
    return this.$route.name && ['Home', 'InCompany'].includes(this.$route.name)
  }

  /**
   * ヘッダー・サイドバーを出さず、ブラウザのネイティブスクロールで見せるページ
   * （学習ダッシュボードなどのレポート画面）。
   */
  get isFullscreenPage(): boolean {
    return this.$route.meta?.fullscreen === true
  }

  get isSurveyPage() {
    return this.$route.name === 'Survey'
  }
}
</script>

<style lang="scss" scoped>
@media (max-width: 991px) {
  .section-layout__content {
    overflow-x: hidden;
  }
}

.section-layout__content {
  &.gray-bg {
    background-color: $cl-333333;
  }
}

@media (min-width: 1281px) {
  .app {
    max-width: 1280px;
    margin: 0 auto;
  }
}

/* 全画面ページはブラウザのネイティブスクロールにする。
   通常ページは .app-wrapper が overflow-y:hidden で画面を固定し、
   各ページが内側にスクロール領域（.height-scroll-fixed 等）を持つ作りになっているため、
   その固定を外さないと縦スクロールできない。 */
.app-wrapper.fullscreen-page {
  height: auto !important;
  min-height: 100%;
  overflow-y: visible !important;
}

.app-wrapper.fullscreen-page .app {
  height: auto !important;
  max-width: none;
}

.app-wrapper.fullscreen-page .flex-container {
  height: auto !important;
}

.scrollable {
  overflow-y: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
